<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                data-test-id="relay-map-navigation-back-button"
                @click="goBack('admin-relays')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Relay Map</h1>
        </header>

        <section class="module__body">
            <p class="description">Click on a Relay to view it's details</p>
            <div
                id="relay-map"
                ref="mapContainer"
                class="map-container"
            >
                <leaflet-map
                    :mapContainer="mapContainer"
                    @mapClicked="clearSelectedRelay"
                >
                    <template #topLeft>
                        <div class="controls">
                            <div
                                v-if="dataIsLoading"
                                class="map-status large"
                                data-test-id="map-data-status-container"
                            >
                                <div
                                    data-test-id="map-data-loading-label"
                                    class="loading-label"
                                >
                                    Loading Relays...
                                </div>
                            </div>

                            <div
                                v-else-if="errorLabel"
                                class="map-status large"
                                data-test-id="map-data-status-container"
                            >
                                <div
                                    data-test-id="map-data-error-container"
                                    class="error-label"
                                >
                                    <span data-test-id="map-data-error-label">
                                        {{ errorLabel }}
                                    </span>
                                    <button
                                        class="btn-small"
                                        data-test-id="map-data-error-retry-button"
                                        type="button"
                                        @click.prevent="retryDataFetch"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>

                            <div
                                v-else-if="lastDataLoadTimestampString"
                                class="map-status minimal"
                                data-test-id="map-data-status-container"
                            >
                                <div
                                    class="last-loaded-timestamp"
                                    data-test-id="map-data-last-loaded-container"
                                >
                                    Last Updated:
                                    <span data-test-id="map-data-last-loaded-label">{{
                                        lastDataLoadTimestampString
                                    }}</span>
                                </div>
                            </div>
                            <RelayInfoPane
                                v-if="selectedRelay"
                                :relay="selectedRelay"
                                @closed="clearSelectedRelay"
                            />
                        </div>
                    </template>
                    <RelayLayer
                        ref="relayLayer"
                        :focusedRelayId="focusedRelayId"
                        @fetchStart="handleFetchStart"
                        @fetchCompleted="handleFetchCompleted"
                        @fetchAllRelaysError="handleFetchAllRelaysError"
                        @fetchFocusedRelayCompleted="handleFetchFocusedRelayCompleted"
                        @fetchFocusedRelayError="handleFetchFocusedRelayError"
                        @selectedRelayChanged="handleSelectedRelayChanged"
                    />
                </leaflet-map>
            </div>
        </section>
    </div>
</template>

<script>
import canToast from '@/mixins/canToast'
import LeafletMap from '@/components/Map/LeafletMap.vue'
import RelayLayer from '@/components/Map/Layers/RelayLayer.vue'
import hasBackButton from '@/mixins/hasBackButton'
import RelayInfoPane from '@/components/Map/Panes/RelayInfoPane.vue'

export default {
    components: {
        LeafletMap,
        RelayLayer,
        RelayInfoPane,
    },

    mixins: [hasBackButton, canToast],

    data() {
        const focusedRelayId = this.$route.query?.relayId ? parseInt(this.$route.query.relayId, 10) : null

        return {
            dataIsLoading: true,
            mapContainer: null,
            focusedRelayId,
            fetchAllRelaysError: false,
            fetchFocusedRelayError: false,
            lastDataLoadTimestamp: null,
            selectedRelay: null,
        }
    },

    computed: {
        errorLabel() {
            if (this.fetchFocusedRelayError) {
                return 'Failed to load requested Relay.'
            }

            if (this.fetchAllRelaysError) {
                return 'Failed to load all Relays.'
            }

            return null
        },

        lastDataLoadTimestampString() {
            if (this.lastDataLoadTimestamp) {
                return window.moment(this.lastDataLoadTimestamp).format('HH:mm:ss [on] ddd MM/DD/YY')
            }

            return null
        },
    },

    async mounted() {
        this.mapContainer = this.$refs.mapContainer
    },

    methods: {
        handleFetchStart() {
            this.dataIsLoading = true
            this.fetchAllRelaysError = false
            this.fetchFocusedRelayError = false
        },
        handleFetchFocusedRelayCompleted(relay) {
            this.$eventBus.emit('center-on', [relay])
            this.fetchFocusedRelayError = false
        },
        handleFetchFocusedRelayError() {
            this.fetchFocusedRelayError = true
        },
        handleFetchAllRelaysError() {
            this.fetchAllRelaysError = true
        },
        handleFetchCompleted({ relays, pageCount }) {
            if (
                !this.focusedRelayId &&
                !this.selectedRelay &&
                this.lastDataLoadTimestamp === null &&
                relays.length > 0 &&
                pageCount === 1
            ) {
                this.$eventBus.emit('center-on', relays)
            }
            this.lastDataLoadTimestamp = Date.now()
            this.dataIsLoading = false
        },
        async retryDataFetch() {
            await this.$refs.relayLayer.refreshData()
        },
        clearSelectedRelay() {
            this.$refs.relayLayer.clearSelectedRelay()
        },
        async handleSelectedRelayChanged({ relay, relayWasDeleted }) {
            if (this.selectedRelay && relayWasDeleted) {
                this.toast(`The relay ${this.selectedRelay.site_name} has been deleted.`, 'info')
            }

            this.selectedRelay = relay

            if (relay) {
                await this.$nextTick()

                this.$eventBus.emit('center-if-covered', relay)
            }
        },
    },
}
</script>
<style lang="scss">
.description {
    color: #bec3dc;
    margin-bottom: 20px;
}

.map-status {
    background-color: #211f26;
    color: #f1f1f1;
    font-size: 16px;
    border-radius: 8px;
    font-family: 'Avenir Light', sans-serif;
    width: fit-content;

    &.large {
        padding: 1em;
    }

    &.minimal {
        padding: 0.6em;
    }

    .loading-label {
        position: relative;

        display: inline-block;

        padding-left: 2.5em;
    }

    .loading-label::before {
        content: '';

        width: 1.5em;
        height: 1.5em;

        border: 0.15em solid #211f26;
        border-top: 0.15em solid #84db82;
        border-radius: 50%;
        transform: translate(-50%, -50%);

        position: absolute;
        left: 0.65em;
        top: 48%;

        animation: spinNoLabel 1s linear infinite;
    }

    .error-label {
        font-size: 16px;

        &:before {
            content: '';
            background-image: url(@images/icon-alert.svg);
            background-size: 17px 17px;
            background-repeat: no-repeat;
            background-position: top left;
            width: 17px;
            height: 17px;
            display: inline-block;
            vertical-align: sub;
            margin-right: 15px;
        }

        & button {
            cursor: pointer;
            font-size: 12px;
            margin-left: 2ex;
            border-width: 0;
            background-color: transparent;
            color: #bec3dc;
        }
    }

    .last-loaded-timestamp {
        font-family: 'Avenir Heavy', sans-serif;
        font-size: 11px;
        color: #d7dffa;
        & span {
            margin-left: 0.5em;
            font-family: 'Avenir Light', sans-serif;
        }
    }
}

.controls {
    display: flex;
    flex-direction: column;
    gap: 1.5ex;
    pointer-events: none;

    & > div {
        pointer-events: auto;
    }
}
</style>
